import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plane, ExternalLink, Info } from "lucide-react";
import { DuffelAncillaries } from "@duffel/components";

/**
 * /duffel-demo — preview the Duffel ancillaries widget while we wait for
 * a Duffel API token. Uses Duffel's built-in fixture offer (`fixture_off_*`)
 * which is self-contained and requires no API key. The page is intentionally
 * NOT linked from the main navigation — share the direct URL with whoever
 * needs to review the UX (you, internal team, Duffel onboarding rep).
 *
 * Once you receive a live Duffel access token, the swap path is:
 *   1. Set `DUFFEL_ACCESS_TOKEN` in backend .env
 *   2. Add backend proxy `/api/duffel/offers/search` → returns a real Offer
 *   3. Replace `offer_id="fixture_off_1"` below with the live offer object +
 *      seat_maps fetched via the backend proxy
 *   4. Hand `onPayloadReady` to a `/api/duffel/orders/create` call to actually
 *      book the ancillaries.
 */
export default function DuffelDemoPage() {
  const [lastPayload, setLastPayload] = useState(null);

  return (
    <div className="page-shell max-w-5xl mx-auto py-12 px-6" data-testid="duffel-demo-page">
      {/* Header */}
      <header className="mb-10">
        <div className="overline flex items-center gap-2">
          <Plane size={14} strokeWidth={1.5} className="text-[var(--gold)]" />
          Duffel · Ancillaries widget · fixture preview
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-3 leading-tight">
          Bags &middot; seats &middot; cancel-for-any-reason
        </h1>
        <p className="text-base text-[var(--ink-2)] mt-4 max-w-2xl leading-relaxed">
          This is a preview of the upsell widget that will surface on the
          acceptance-success page once Janté is approved for live Duffel API
          access. The data below is rendered against Duffel's own
          <code className="mx-1 px-1.5 py-0.5 bg-[var(--ivory-2)] text-[var(--brand-red)] text-xs">fixture_off_1</code>
          test offer — no real bookings can be made yet.
        </p>
      </header>

      {/* Status callout */}
      <div className="border-l-2 border-[var(--gold)] bg-[var(--ivory-2)] px-5 py-4 flex items-start gap-3 mb-10">
        <Info size={16} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
        <div className="text-sm text-[var(--ink)] leading-relaxed">
          <p>
            <strong>Status:</strong> awaiting Duffel API token. The component is
            installed and ready (
            <code className="text-xs text-[var(--brand-red)]">@duffel/components 3.16.1</code>) —
            once we receive credentials, we'll swap the fixture for live offers
            in a single deploy.
          </p>
          <p className="mt-2 text-[var(--ink-2)] text-xs">
            Apply for Duffel access here:{" "}
            <a
              href="https://duffel.com/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--brand-red)] underline hover:text-[var(--emerald)] inline-flex items-center gap-1"
            >
              duffel.com/signup <ExternalLink size={10} strokeWidth={1.75} />
            </a>
          </p>
        </div>
      </div>

      {/* The actual widget */}
      <section className="duffel-shell" data-testid="duffel-ancillaries-widget">
        <DuffelAncillaries
          debug
          offer_id="fixture_off_1"
          services={["bags", "seats", "cancel_for_any_reason"]}
          passengers={[
            {
              id: "pas_0000AUde3KY1SptM6ABSfU",
              given_name: "Mae",
              family_name: "Jemison",
              gender: "F",
              title: "dr",
              born_on: "1956-10-17",
              email: "m.jemison@nasa.gov",
              phone_number: "+16177562626",
            },
            {
              id: "pas_0000AUde3KY1SptM6ABSfT",
              given_name: "Dorothy",
              family_name: "Green",
              gender: "F",
              title: "dr",
              born_on: "1942-10-17",
            },
          ]}
          onPayloadReady={(payload) => {
            // In production: POST payload → /api/duffel/orders/create which
            // forwards to Duffel and returns the created order. For now we
            // just preview the payload below so the integrator can confirm
            // the shape.
            console.log("Duffel payload ready", payload);
            setLastPayload(payload);
          }}
        />
      </section>

      {/* Payload debug — appears once the guest taps "Continue" on the widget */}
      {lastPayload && (
        <section className="mt-10" data-testid="duffel-payload-preview">
          <div className="overline mb-2">Last payload from onPayloadReady</div>
          <pre className="bg-[var(--emerald)] text-[var(--ivory)] p-5 text-[11px] leading-relaxed overflow-auto max-h-96">
            {JSON.stringify(lastPayload, null, 2)}
          </pre>
          <p className="text-xs text-[var(--ink-2)] mt-2">
            In production this payload is forwarded to{" "}
            <code className="text-[var(--brand-red)]">POST /api/duffel/orders/create</code>{" "}
            to actually book the ancillaries.
          </p>
        </section>
      )}

      <div className="mt-12 pt-8 border-t border-[var(--emerald)]/15">
        <Link
          to="/Talk-To-Sasha"
          className="text-xs uppercase tracking-[0.2em] text-[var(--brand-red)] hover:text-[var(--emerald)]"
        >
          ← Back to Sasha
        </Link>
      </div>
    </div>
  );
}
